import React, { useState } from "react";
import DeleteAccountSheet from "./DeleteAccountSheet";
import { signInLabel } from "./profileFormat";
import { formatCount, initials } from "../ui/text";
import "../ui/tokens.css";
import "./profile.css";

function Row({ label, value, onClick, tone, hint }) {
  const content = (
    <>
      <span className="pf-row-label">{label}</span>
      {value ? <span className="pf-row-value" title={value}>{value}</span> : null}
      {hint ? <span className="pf-row-hint">{hint}</span> : null}
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={`pf-row pf-row--action${tone === "danger" ? " is-danger" : ""}`} onClick={onClick}>
        {content}
      </button>
    );
  }
  return <div className="pf-row">{content}</div>;
}

function SwitchRow({ label, checked, onChange }) {
  return (
    <button type="button" className="pf-row pf-row--action" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}>
      <span className="pf-row-label">{label}</span>
      <span className={`pf-switch${checked ? " is-on" : ""}`} aria-hidden="true" />
    </button>
  );
}

// The student's Profile: who they are, account facts, preferences, privacy and data, and the account actions.
export default function ProfileScreen({
  userName,
  email,
  avatarUrl,
  signInMethod,
  memberSince,
  messageCount,
  chatCount,
  inSchool,
  darkMode,
  avatarInputRef,
  onAvatarChange,
  onBack,
  onToggleDark,
  onChangePassword,
  onExport,
  onDeleteAllData,
  onOpenTerms,
  onDeleteAccount,
  onLogout,
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const badge = signInLabel(signInMethod);

  return (
    <div className="pf">
      <header className="pf-bar">
        <button type="button" className="pf-back" onClick={onBack}>
          Chat
        </button>
        <h1 className="pf-title">Profile</h1>
        <span className="pf-bar-space" />
      </header>

      <div className="pf-scroll">
        <div className="pf-col">
          <section className="pf-card" aria-label="You">
            <div className="pf-id">
              <button type="button" className="pf-avatar" aria-label="Change profile photo" onClick={() => avatarInputRef && avatarInputRef.current && avatarInputRef.current.click()}>
                {avatarUrl ? <img src={avatarUrl} alt="" /> : initials(userName)}
                <span className="pf-avatar-plus" aria-hidden="true">+</span>
              </button>
              <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={onAvatarChange} />
              <div className="pf-id-text">
                <h2 className="pf-name">{userName}</h2>
                {email ? <p className="pf-email">{email}</p> : null}
                {badge ? <span className="pf-badge">{badge}</span> : null}
              </div>
            </div>
            <div className="pf-stats">
              <div>
                <b>{formatCount(messageCount || 0)}</b>
                <small>{messageCount === 1 ? "message" : "messages"}</small>
              </div>
              {chatCount !== null && chatCount !== undefined ? (
                <div>
                  <b>{formatCount(chatCount)}</b>
                  <small>{chatCount === 1 ? "chat" : "chats"}</small>
                </div>
              ) : null}
            </div>
          </section>

          <h3 className="pf-sec">Account</h3>
          <section className="pf-card pf-rows">
            {email ? <Row label="Email" value={email} /> : null}
            {signInMethod === "password" ? <Row label="Password" hint="Change" onClick={onChangePassword} /> : null}
            {signInMethod === "google" ? <Row label="Password" value="Managed by Google" /> : null}
            {memberSince ? <Row label="Member since" value={memberSince} /> : null}
          </section>

          <h3 className="pf-sec">Preferences</h3>
          <section className="pf-card pf-rows">
            <SwitchRow label="Dark mode" checked={!!darkMode} onChange={onToggleDark} />
          </section>

          <h3 className="pf-sec">Privacy and your data</h3>
          <div className="pf-note">
            <b>Your chats are private.</b>
            Your school never sees what you write. Replies are written by an AI service, so your messages are sent to it to get a reply.
          </div>
          {inSchool ? (
            <div className="pf-note">
              <b>What your school can see</b>
              <ul>
                <li>That you're enrolled</li>
                <li>Dates you were active</li>
                <li>Combined trends, such as how many chats were flagged for support</li>
              </ul>
              Never what you write.
            </div>
          ) : null}
          <section className="pf-card pf-rows">
            <Row label="Download my data" hint="Export" onClick={onExport} />
            <Row label="Delete all my data" hint="Delete" tone="danger" onClick={onDeleteAllData} />
            <Row label="Terms and Privacy Policy" hint="View" onClick={onOpenTerms} />
          </section>

          <button type="button" className="pf-btn pf-btn--danger" onClick={() => setDeleteOpen(true)}>
            Delete my account
          </button>
          <button type="button" className="pf-btn" onClick={onLogout}>
            Log out
          </button>

          <p className="pf-foot">
            Rigby is not a therapist. Crisis? Call or text <a href="tel:988">988</a>.
            <br />
            Questions about your data? <a href="mailto:hello@myemotionalsupportbot.com">hello@myemotionalsupportbot.com</a>
            <br />© {new Date().getFullYear()} My Emotional Support Bot
          </p>
        </div>
      </div>

      <DeleteAccountSheet
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onExport={onExport}
        onConfirm={() => {
          setDeleteOpen(false);
          onDeleteAccount();
        }}
      />
    </div>
  );
}
